import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BackendRoute } from '../config/index';
import { QueryKey } from '../constants/index';
import { ApiService } from '../services/index';
import { PoolPlansTab, SharePlanUnionType } from '../types/pool-plans.types';

export const useSharePlans = ({
  companyId,
  numberOfFetchedItems = 50,
  poolId,
  selectedTab = PoolPlansTab.ACTIVE,
  enabled = true,
  vestByDate,
}: {
  companyId: string;
  enabled?: boolean;
  numberOfFetchedItems?: number;
  poolId: string;
  selectedTab?: PoolPlansTab;
  vestByDate?: Date;
}) => {
  const {
    data: sharePlansData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useInfiniteQuery<{
    sharePlans: SharePlanUnionType[];
    totalPages: number;
  }>({
    queryKey: [QueryKey.GET_SHARE_PLANS, companyId, poolId, selectedTab, vestByDate],
    queryFn: ({ pageParam = 0 }) => {
      return ApiService.get(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, 'pools', poolId, 'share-plans'],
        },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip: pageParam,
            isTerminated: selectedTab === PoolPlansTab.TERMINATED,
            vestByDate,
          },
        },
      );
    },
    enabled: !!poolId && !!companyId && enabled,
    initialPageParam: 0,
    refetchOnReconnect: true,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.totalPages > allPages.length ? allPages.length * numberOfFetchedItems : undefined,
  });
  const sharePlans = useMemo(
    () =>
      sharePlansData?.pages?.reduce<SharePlanUnionType[]>((acc, page) => {
        return [...acc, ...page.sharePlans];
      }, []),
    [sharePlansData?.pages],
  );

  return {
    sharePlans: sharePlans || [],
    hasNextPage,
    fetchNextPage,
    sharePlansData,
    isEmpty: sharePlans?.length === 0,
    isLoading,
    numberOfFetchedItems,
  };
};
